<template>
  <d-container
    fluid
    class="main-content-container px-0 px-md-2 px-lg-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        sm="12"
        md="6"
        lg="6"
        class="text-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Klienci</span>
        <h3 class="page-title">
          <template v-if="isEdit">Edycja klienta</template>
          <template v-else>Dodawanie klienta</template>
        </h3>
      </d-col>
      <d-col
        sm="12"
        md="6"
        lg="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/klienci')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
        <d-button
          :disabled="validationResults !== ''"
          theme="success"
          @click.prevent="validateForm">
          Zapisz zmiany
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <div
          v-if="!allDataLoaded"
          class="loading-spinner pb-3 pt-3 text-center">
          <span class="loading-spinner-content text-primary">
            Trwa ładowanie danych&hellip;
          </span>
        </div>
        <div
          v-if="nipResultsLoading"
          class="loading-spinner pb-3 pt-3 text-center">
          <span class="loading-spinner-content text-primary">
            Trwa wyszukiwanie danych&hellip;
          </span>
        </div>
        <validation-observer
          ref="client-form"
          tag="form">
          <d-tabs
            card
            :nav-class="allDataLoaded ? validationResults : ''"
            :class="{'is-loading': !allDataLoaded || nipResultsLoading}">
            <!-- Dane podstawowe -->
            <d-tab
              title="Dane podstawowe"
              active
              class="width-narrow px-3 px-md-4">
              <h4 class="mb-3 pb-3 border-bottom">Dane podstawowe</h4>

              <d-alert
                v-if="nipResultsEmpty"
                show
                theme="warning">
                Nie znaleziono firmy o podanym numerze NIP.
              </d-alert>

              <d-row class="mb-3">
                <d-col
                  lg="6"
                  md="6">
                  <div class="form-group">
                    <label for="nip-load-data">Pobierz dane na podstawie NIP:</label>

                    <validation-provider
                      :rules="nipLoadData && nipLoadData.length ? 'length:10|only-numbers' : ''"
                      name="nip-load-data"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="nipLoadData"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col>
                  <div class="form-group nipSearchBtn">
                    <d-button
                      :disabled="!isNipLoadDataValid"
                      class="ml-auto"
                      theme="primary"
                      @click.prevent="fetchInvoiceInfo">
                      Pobierz dane
                    </d-button>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  lg="6"
                  md="6">
                  <div class="form-group">
                    <label for="name">Nazwa firmy:</label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.name"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col
                  lg="6"
                  md="6">
                  <div class="form-group">
                    <label for="surname">Imię i Nazwisko:</label>

                    <validation-provider
                      rules=""
                      name="surname"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.surname"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  lg="6"
                  md="6">
                  <div class="form-group">
                    <label for="street">Ulica:</label>

                    <validation-provider
                      rules="required"
                      name="street"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.street"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  lg="3"
                  md="3">
                  <div class="form-group">
                    <label for="building_number">Nr budynku:</label>

                    <validation-provider
                      rules=""
                      name="building_number"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.building_number"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  lg="3"
                  md="3">
                  <div class="form-group">
                    <label for="house_number">Nr lokalu:</label>

                    <validation-provider
                      name="house_number"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.house_number"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  lg="9"
                  md="9">
                  <div class="form-group">
                    <label for="city">Miejscowość:</label>

                    <validation-provider
                      rules="required"
                      name="city"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.city"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  lg="3"
                  md="3">
                  <div class="form-group">
                    <label for="post_code">Kod pocztowy:</label>

                    <validation-provider
                      rules="required"
                      name="post_code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.post_code"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  lg="6"
                  md="6">
                  <div class="form-group">
                    <label for="email">Adres e-mail:</label>

                    <validation-provider
                      rules="required|email"
                      name="email"
                      type="email"
                      v-slot="{ errors }">
                      <d-input
                        v-model="data.email"
                        name="email"
                        type="email"
                        :state="errors.length ? 'invalid' : null"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  lg="6"
                  md="6">
                  <div class="form-group">
                    <label for="phone">Telefon kontaktowy:</label>

                    <validation-provider
                      rules="required"
                      name="phone"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.phone"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
              <d-row>
                <d-col sm="12">
                  <div
                    v-if="isAdmin"
                    class="form-group">
                    <label for="worker">Pracownik przypisany do klienta:</label>

                    <validation-provider
                      name="worker"
                      rules="required"
                      v-slot="{ errors }">
                      <div
                        :class="{
                          'form-control': true,
                          'is-select': true,
                          'is-invalid': errors.length,
                          'vs-wrap-text': true
                        }">
                        <v-select
                          :disabled="!loadedWorkers"
                          :options="workers"
                          v-model="data.worker_id"
                          :reduce="(worker) => worker.id"
                          placeholder="Wybierz pracownika"
                          :searchable="true"
                          label="name">
                        </v-select>
                      </div>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col sm="12">
                  <div class="form-group">
                    <label for="payment-methods">Metody płatności: </label>

                    <validation-provider
                      rules="required"
                      name="payment-methods"
                      v-slot="{ errors }">
                      <div
                        :class="{
                          'form-control': true,
                          'is-select': true,
                          'is-invalid': errors.length,
                          'vs-wrap-text': true
                        }">
                        <v-select
                          :disabled="isWorker"
                          :options="allPayments"
                          v-model="data.payments"
                          :reduce="(payment) => payment.id"
                          placeholder="Wybierz metody płatności"
                          :searchable="true"
                          :multiple="true"
                          label="name">
                        </v-select>
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  sm="12"
                  class="text-left mt-2">
                  <d-checkbox
                    inline
                    v-model="data.send_products_stock"
                    toggle
                    toggle-small>
                    Wysyłaj informację o dostępności produktów
                  </d-checkbox>
                </d-col>
              </d-row>

              <div
                v-if="!allDataLoaded"
                class="pb-3 pt-3 text-center">
                Trwa ładowanie danych&hellip;
              </div>

              <d-alert
                v-if="loadError"
                show
                theme="warning">
                Wczytywanie danych nie powiodło się.
                <a
                  href="javascript:window.location.reload();"
                  class="alert-link">
                  Odśwież stronę
                </a>
                aby spróbować ponownie.
              </d-alert>
            </d-tab>
            <!-- Rabaty klienta -->
            <d-tab
              title="Rabaty klienta"
              class="width-medium px-3 px-md-4">
              <h4 class="mb-3 pb-3 border-bottom">Rabaty klienta</h4>
              <div class="form-group border-bottom mb-3">
                <d-row>
                  <d-col sm="4">
                    <div class="form-group">
                      <label for="discount">Rabat klienta:</label>

                      <validation-provider
                        type="number"
                        name="discount"
                        rules="max_value:100|min_value:0"
                        v-slot="{ errors }">
                        <d-input-group append="%">
                          <d-input
                            class="text-right"
                            :disabled="!isAdmin"
                            min="0"
                            max="100"
                            step="1"
                            type="number"
                            :state="errors.length ? 'invalid' : null"
                            v-model="data.discount"/>
                        </d-input-group>
                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>
                    </div>
                  </d-col>
                </d-row>
              </div>
              <div class="form-group border-bottom mb-3 pb-3">
                <d-row>
                  <d-col sm="8" class="d-flex">
                    <h5 class="my-auto h6">
                      Rabaty dla produktów ({{ $refs['product-discount'] ? $refs['product-discount'].getDiscountsAmount() : 0 }}):
                    </h5>
                  </d-col>
                  <d-col sm="4">
                    <d-button
                      v-if="isAdmin"
                      theme="success"
                      style="float: right;"
                      :disabled="!loadedCategories"
                      @click.prevent="$refs['product-discount'].addDiscount()">
                      <i class="material-icons" style="font-size: 14px;">add</i>
                      Dodaj rabat
                    </d-button>
                  </d-col>
                </d-row>
                <customer-product-discount
                  v-if="allDataLoaded && isAdmin"
                  ref="product-discount"
                  :disabled="!loadedCategories || !isAdmin"
                  :allProducts = "allProducts"
                  :currentProducts = "data.products" />
                <customer-product-discount-info
                  v-if="allDataLoaded && !isAdmin"
                  ref="product-discount-info"
                  :disabled="!loadedCategories || !isAdmin"
                  :allProducts = "allProducts"
                  :productsDiscounts = "data.products" />
              </div>
              <div class="form-group border-bottom mb-3 pb-3">
                <d-row>
                  <d-col sm="8" class="d-flex">
                    <h5 class="my-auto h6">
                      Rabaty producentów ({{ $refs['manufacturer-discount'] ? $refs['manufacturer-discount'].getDiscountsAmount() : 0 }}):
                    </h5>
                  </d-col>
                  <d-col sm="4">
                    <d-button
                      v-if="isAdmin"
                      class="ml-auto"
                      theme="success"
                      style="float: right;"
                      :disabled="!loadedManufacturers"
                      @click.prevent="$refs['manufacturer-discount'].addDiscount()">
                      <i class="material-icons">add</i>
                      Dodaj rabat
                    </d-button>
                  </d-col>
                </d-row>
                <customer-manufacturer-discount
                  v-if="allDataLoaded && isAdmin"
                  ref="manufacturer-discount"
                  :disabled="!loadedManufacturers || !isAdmin"
                  :allManufacturers = "allManufacturers"
                  :currentManufacturers = "data.manufacturers"/>
                <customer-manufacturer-discount-info
                  v-if="allDataLoaded && !isAdmin"
                  ref="manufacturer-discount-info"
                  :allManufacturers = "allManufacturers"
                  :manufacturersDiscounts = "data.manufacturers"/>
              </div>
              <div class="form-group mb-3 pb-3">
                <d-row>
                  <d-col sm="8" class="d-flex">
                    <h5 class="my-auto h6">
                      Rabaty dla kategorii produktów ({{ $refs['category-discount'] ? $refs['category-discount'].getDiscountsAmount() : 0 }}):
                    </h5>
                  </d-col>
                  <d-col sm="4">
                    <d-button
                      v-if="isAdmin"
                      theme="success"
                      style="float: right;"
                      :disabled="!loadedCategories"
                      @click.prevent="$refs['category-discount'].addDiscount()">
                      <i class="material-icons" style="font-size: 14px;">add</i>
                      Dodaj rabat
                    </d-button>
                  </d-col>
                </d-row>
                <customer-category-discount
                  v-if="allDataLoaded && isAdmin"
                  ref="category-discount"
                  :disabled="!loadedCategories || !isAdmin"
                  :allCategories = "allCategories"
                  :currentCategories = "data.categories" />
                <customer-category-discount-info
                  v-if="allDataLoaded && !isAdmin"
                  ref="category-discount-info"
                  :allCategories = "allCategories"
                  :categoriesDiscounts = "data.categories"/>
              </div>

              <div
                v-if="!allDataLoaded"
                class="pb-3 pt-3 text-center">
                Trwa ładowanie danych&hellip;
              </div>

              <d-alert
                v-if="loadError"
                show
                theme="warning">
                Wczytywanie danych nie powiodło się.
                <a
                  href="javascript:window.location.reload();"
                  class="alert-link">
                  Odśwież stronę
                </a>
                aby spróbować ponownie.
              </d-alert>
            </d-tab>
            <!-- Dane do faktury -->
            <d-tab
              title="Dane do faktury"
              class="width-narrow px-3 px-md-4">
              <h4 class="mb-3 pb-3 border-bottom">Dane do faktury</h4>
              <d-row>
                <d-col
                  lg="8"
                  md="8">
                  <div class="form-group">
                    <label for="invoice_company">Nazwa firmy:</label>

                    <validation-provider
                      rules="required"
                      name="invoice_company"
                      v-slot="{ errors }">
                      <d-input
                        :disabled="!isAdmin"
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.invoice_company"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col
                  lg="4"
                  md="4">
                  <div class="form-group">
                    <label for="invoice_nip">NIP:</label>

                    <validation-provider
                      rules="required|length:10|only-numbers"
                      name="invoice_nip"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.invoice_nip"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  lg="6"
                  md="6">
                  <div class="form-group">
                    <label for="invoice_name">Imię:</label>

                    <validation-provider
                      name="invoice_name"
                      v-slot="{ errors }">
                      <d-input
                        :disabled="!isAdmin"
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.invoice_name"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col
                  lg="6"
                  md="6">
                  <div class="form-group">
                    <label for="invoice_surname">Nazwisko:</label>

                    <validation-provider
                      name="invoice_surname"
                      v-slot="{ errors }">
                      <d-input
                        :disabled="!isAdmin"
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.invoice_surname"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  lg="6"
                  md="6">
                  <div class="form-group">
                    <label for="invoice_street">Ulica:</label>

                    <validation-provider
                      rules="required"
                      name="invoice_street"
                      v-slot="{ errors }">
                      <d-input
                        :disabled="!isAdmin"
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.invoice_street"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  lg="3"
                  md="3">
                  <div class="form-group">
                    <label for="invoice_building_number">Nr budynku:</label>

                    <validation-provider
                      rules=""
                      name="invoice_building_number"
                      v-slot="{ errors }">
                      <d-input
                        :disabled="!isAdmin"
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.invoice_building_number"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  lg="3"
                  md="3">
                  <div class="form-group">
                    <label for="invoice_house_number">Nr lokalu:</label>

                    <validation-provider v-slot="{ errors }">
                      <d-input
                        :disabled="!isAdmin"
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.invoice_house_number"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  lg="9"
                  md="9">
                  <div class="form-group">
                    <label for="invoice_city">Miejscowość:</label>

                    <validation-provider
                      rules="required"
                      name="invoice_city"
                      v-slot="{ errors }">
                      <d-input
                        :disabled="!isAdmin"
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.invoice_city"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  lg="3"
                  md="3">
                  <div class="form-group">
                    <label for="invoice_post_code">Kod pocztowy:</label>

                    <validation-provider
                      rules="required"
                      name="invoice_post_code"
                      v-slot="{ errors }">
                      <d-input
                        :disabled="!isAdmin"
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.invoice_post_code"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <div
                v-if="!allDataLoaded"
                class="pb-3 pt-3 text-center">
                Trwa ładowanie danych&hellip;
              </div>

              <d-alert
                v-if="loadError"
                show
                theme="warning">
                Wczytywanie danych nie powiodło się.
                <a
                  href="javascript:window.location.reload();"
                  class="alert-link">
                  Odśwież stronę
                </a>
                aby spróbować ponownie.
              </d-alert>
            </d-tab>

            <!-- Osoby kontaktowe -->
            <d-tab title="Osoby kontaktowe">
              <h4
                v-if="isEdit"
                class="pb-3 mb-0 border-bottom d-flex" >
                Osoby kontaktowe
                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="showAddContactPerson">
                  <i class="material-icons">add</i>
                  Dodaj kontakt
                </d-button>
              </h4>

              <d-alert
                v-if="!isEdit"
                show
                theme="warning">
                Dodawanie osób kontaktowych będzie możliwe po zapisaniu klienta.
              </d-alert>

              <div v-if="isEdit">
                <table class="table mt-0 mb-0 full-width">
                  <thead class="bg-light">
                    <tr>
                      <th scope="col" class="border-0 align-middle">Imię i nazwisko</th>
                      <th scope="col" class="border-0 align-middle text-center">Adres e-mail</th>
                      <th scope="col" class="border-0 align-middle text-center">Telefon</th>
                      <th scope="col" class="border-0 align-middle">Notatka</th>
                      <th scope="col" class="border-0 align-middle text-center" style="width: 150px;">Akcje</th>
                    </tr>
                  </thead>
                  <tbody v-if="loaded">
                    <tr
                      v-for="(contact, index) of data.contacts"
                      :key="'contact-' + index">
                      <td class="align-middle py-lg-1 py-md-3 py-3">
                        <label class="table-mobile-label mb-1">Imię i nazwisko</label>
                        {{ contact.name }} {{ contact.surname }}
                      </td>
                      <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                        <label class="table-mobile-label mb-1">Adres e-mail</label>
                        <template v-if="contact.email">
                          <a :href="'mailto:' + contact.email">
                            {{ contact.email }}
                          </a>
                        </template>
                        <template v-else>
                          Brak
                        </template>
                      </td>
                      <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                        <label class="table-mobile-label mb-1">Telefon</label>
                        <template v-if="contact.phone">
                          <a :href="'tel:' + contact.phone">
                            {{ contact.phone }}
                          </a>
                        </template>
                        <template v-else>
                          Brak
                        </template>
                      </td>
                      <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                        <label class="table-mobile-label mb-1">Notatka</label>
                        <template v-if="contact.notes">
                          <small>{{ contact.notes }}</small>
                        </template>
                        <template v-else>
                          -
                        </template>
                      </td>
                      <td class="text-center align-middle pt-0">
                        <d-button
                          theme="primary"
                          @click.prevent="
                            $bus.$emit(
                              'client-contact-person-popup-show',
                              data.id,
                              contact)"
                          class="mx-2 mt-2"
                          title="Edytuj">
                          <i class="material-icons">edit</i>
                        </d-button>
                        <d-button
                          theme="danger"
                          :disabled="removeContactInProgress"
                          @click.prevent="removeContact(contact.id)"
                          title="Usuń"
                          class="mx-2 mt-2">
                          <i class="material-icons">delete</i>
                        </d-button>
                      </td>
                    </tr>
                    <tr v-if="data.contacts.length === 0">
                      <td
                        colspan="5"
                        class="text-center">
                        Brak kontaktów do wyświetlenia&hellip;
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                v-if="!loaded"
                class="pb-3 pt-3 text-center">
                Trwa ładowanie danych&hellip;
              </div>

              <d-alert
                v-if="loadError"
                show
                theme="warning">
                Wczytywanie danych nie powiodło się.
                <a
                  href="javascript:window.location.reload();"
                  class="alert-link">
                  Odśwież stronę
                </a>
                aby spróbować ponownie.
              </d-alert>
            </d-tab>

            <!-- Adresy dostawy -->
            <d-tab title="Adresy dostawy">
              <h4
                v-if="isEdit"
                class="pb-3 mb-0 border-bottom d-flex">
                Adresy dostawy
                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="showAddDeliveryAddress">
                  <i class="material-icons">add</i>
                  Dodaj adres
                </d-button>
              </h4>
              <d-alert
                v-if="!isEdit"
                show
                theme="warning">
                Dodawanie adresów dostawy będzie możliwe po zapisaniu klienta.
              </d-alert>
              <div v-if="isEdit">
                <table class="table mt-0 mb-0 full-width">
                  <thead class="bg-light">
                    <tr>
                      <th scope="col" class="border-0 align-middle">Nazwa firmy</th>
                      <th scope="col" class="border-0 align-middle text-center">Adres</th>
                      <th scope="col" class="border-0 align-middle text-center" style="width: 150px;">Akcje</th>
                    </tr>
                  </thead>
                  <tbody v-if="loaded && loadedAddresses">
                    <tr
                      v-for="(address, index) of data.addresses"
                      :key="'address-' + index">
                      <td class="align-middle py-lg-1 py-md-3 py-3">
                        <label class="table-mobile-label mb-1">Nazwa firmy</label>
                        {{ address.name }}
                      </td>
                      <td class="align-middle text-center py-lg-1 py-md-3 py-3">
                        <label class="table-mobile-label mb-1">Adres</label>
                        {{ address.street }} {{ address.building_number }}
                        <template v-if="address.house_number"> / {{ address.house_number }}</template><br>
                        <small>{{ address.post_code }}, {{ address.city }}</small>
                      </td>
                      <td class="text-center align-middle pt-0">
                        <d-button
                          theme="primary"
                          @click.prevent="
                            $bus.$emit(
                              'client-delivery-address-popup-show',
                              data.id,
                              address
                            )
                          "
                          class="mx-2 mt-2"
                          title="Edytuj">
                          <i class="material-icons">edit</i>
                        </d-button>
                        <d-button
                          theme="danger"
                          :disabled="removeAddressInProgress"
                          @click.prevent="removeAddress(address.id)"
                          title="Usuń"
                          class="mx-2 mt-2">
                          <i class="material-icons">delete</i>
                        </d-button>
                      </td>
                    </tr>
                    <tr v-if="data.addresses.length === 0">
                      <td
                        colspan="3"
                        class="text-center">
                        Brak adresów do wyświetlenia&hellip;
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                v-if="isEdit && (!allDataLoaded)"
                class="pb-3 pt-3 text-center">
                Trwa ładowanie danych&hellip;
              </div>

              <d-alert
                v-if="loadError"
                show
                theme="warning">
                Wczytywanie danych nie powiodło się.
                <a
                  href="javascript:window.location.reload();"
                  class="alert-link">
                  Odśwież stronę
                </a>
                aby spróbować ponownie.
              </d-alert>
            </d-tab>
          </d-tabs>
        </validation-observer>
      </d-card>
    </d-form>

    <client-contact-person-popup />
    <client-delivery-address-popup />
  </d-container>
</template>

<script>
import Vue from 'vue';
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import ClientContactPersonPopup from './../popups/ClientContactPersonPopup.vue';
import ClientDeliveryAddressPopup from './../popups/ClientDeliveryAddressPopup.vue';
import CustomerManufacturerDiscount from './client/CustomerManufacturerDiscount.vue';
import CustomerManufacturerDiscountInfo from './client/CustomerManufacturerDiscountInfo.vue';
import CustomerCategoryDiscount from './client/CustomerCategoryDiscount.vue';
import CustomerCategoryDiscountInfo from './client/CustomerCategoryDiscountInfo.vue';
import CustomerProductDiscount from './client/CustomerProductDiscount.vue';
import CustomerProductDiscountInfo from './client/CustomerProductDiscountInfo.vue';

export default {
  name: 'client-form',
  components: {
    'client-contact-person-popup': ClientContactPersonPopup,
    'client-delivery-address-popup': ClientDeliveryAddressPopup,
    'customer-manufacturer-discount': CustomerManufacturerDiscount,
    'customer-manufacturer-discount-info': CustomerManufacturerDiscountInfo,
    'customer-category-discount': CustomerCategoryDiscount,
    'customer-category-discount-info': CustomerCategoryDiscountInfo,
    'customer-product-discount': CustomerProductDiscount,
    'customer-product-discount-info': CustomerProductDiscountInfo
  },
  computed: {
    allDataLoaded() {
      return this.loaded && this.allManufacturers && this.allCategories && this.allProducts && this.allPayments;
    },
    isEdit() {
      return !!this.$route.params.id;
    },
    isAdmin () {
      return this.$store.getters['getUserType'] === 'ADMIN';
    },
    validationResults() {
      if (!this.allDataLoaded) {
        return false;
      }
      let errors = this.$refs['client-form'].errors;
      let errorTabs = [];
      let results = [
        [
          'error-1',
          [
            'phone',
            'email',
            'street',
            'building_number',
            'city',
            'post_code',
          ],
        ],
        [
          'error-3',
          [
            'invoice_company',
            'invoice_street',
            'invoice_building_number',
            'invoice_city',
            'invoice_post_code',
            'invoice_nip',
          ],
        ],
      ];
      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i][1].length; j++) {
          let key = results[i][1][j];
          if (errors[key] && errors[key].length) {
            errorTabs.push(results[i][0]);
            break;
          }
        }
      }
      return errorTabs.join(' ');
    },
    isWorker () {
      return this.$store.getters['getUserType'] === 'WORKER';
    },
    isNipLoadDataValid () {
      if (!this.nipLoadData) {
        return false;
      }

      if (this.$refs['client-form'] && this.$refs['client-form'].errors && !this.$refs['client-form'].errors['nip-load-data'].length) {
        return true;
      }

      return false;
    }
  },
  watch: {
    loadedPayments (newValue) {
      if (newValue && !this.isEdit && this.isWorker) {
        let paymentCod = this.allPayments.find(payment => payment.woocommerce_id === 'cod');
        if (paymentCod) {
          this.data.payments.push(paymentCod.id);
        }
      }
    }
  },
  data() {
    return {
      data: {
        id: 0,
        worker_id: null,
        name: '',
        surname: '',
        street: '',
        building_number: '',
        house_number: '',
        post_code: '',
        city: '',
        email: '',
        phone: '',
        discount: 0,
        invoice_company: '',
        invoice_name: '',
        invoice_surname: '',
        invoice_street: '',
        invoice_building_number: '',
        invoice_house_number: '',
        invoice_post_code: '',
        invoice_city: '',
        invoice_nip: '',
        worker: {},
        contacts: [],
        addresses: [],
        payments: [],
        categories: [],
        manufacturers: [],
        products: [],
        send_products_stock: false
      },
      loaded: false,
      loadError: false,
      loadedWorkers: false,
      loadedContacts: false,
      loadedAddresses: false,
      removeInProgress: false,
      removeContactInProgress: false,
      removeAddressInProgress: false,
      workers: [],
      allManufacturers: [],
      allCategories: [],
      allProducts: [],
      allPayments: [],
      loadedManufacturers: false,
      loadedCategories: false,
      loadedProducts: false,
      loadedPayments: false,
      invoiceInfo: [],
      loadedInvoiceInfo: false,
      nipLoadData: null,
      nipResultsEmpty: false,
      nipResultsLoading: false
    };
  },
  mounted() {
    if (this.isEdit) {
      this.loadItemData();
    } else {
      this.loaded = true;
    }
    this.loadAdditionalData();
    this.$bus.$on('client-contact-person-added', this.reloadContactList);
    this.$bus.$on('client-delivery-address-added', this.reloadAddressList);
  },
  methods: {
    loadItemData() {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/api/contacts/item/',
        instanceFields: [
          'id',
          'worker_id',
          'name',
          'surname',
          'street',
          'building_number',
          'house_number',
          'post_code',
          'city',
          'email',
          'phone',
          'discount',
          'invoice_company',
          'invoice_name',
          'invoice_surname',
          'invoice_street',
          'invoice_building_number',
          'invoice_house_number',
          'invoice_post_code',
          'invoice_city',
          'invoice_nip',
          'worker',
          'contacts',
          'addresses',
          'payments',
          'categories',
          'manufacturers',
          'products',
          'send_products_stock'
        ],
        booleanFields: ['send_products_stock'],
        loadedKey: 'loaded',
        successAction: () => {
          this.loadedContacts = true;
          this.loadedAddresses = true;
          Vue.nextTick(() => {
            this.data.payments = this.data.payments.map(payment => payment.id)
            this.data.send_products_stock = !!this.data.send_products_stock;

            if (this.isAdmin) {
              this.checkAssignedWorker();
            }
          });
        },
      });
    },
    validateForm() {
      FormUtils.validate(this.$refs['client-form'], this.submitForm);
    },
    submitForm() {
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isEdit
        ? '/api/contacts/update/' + this.id
        : '/api/contacts/store';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano dane klienta',
        successText: 'Wybrany klient został zapisany',
        errorTitle: 'Wystąpił błąd',
        errorText:
          'Modyfikacja danych klienta nie powiodła się. Spróbuj ponownie.',
        successAction: () => {
          this.$router.push('/klienci');
        },
      });
    },
    prepareFormDataToSend() {
      let workerID = this.data.worker_id;
      if (!this.isAdmin) {
        workerID = this.$store.getters['getUserID'];
      }

      let formDataToSend = {
        worker_id: workerID,
        name: this.data.name,
        surname: this.data.surname,
        email: this.data.email,
        phone: this.data.phone,
        street: this.data.street,
        building_number: this.data.building_number,
        house_number: this.data.house_number,
        post_code: this.data.post_code,
        city: this.data.city,
        invoice_company: this.data.invoice_company,
        invoice_street: this.data.invoice_street,
        invoice_building_number: this.data.invoice_building_number,
        invoice_post_code: this.data.invoice_post_code,
        invoice_city: this.data.invoice_city,
        invoice_nip: this.data.invoice_nip,
        invoice_name: this.data.invoice_name,
        invoice_surname: this.data.invoice_surname,
        invoice_house_number: this.data.invoice_house_number,
        payments: this.data.payments,
        discount: this.data.discount,
        send_products_stock: this.data.send_products_stock ? 1 : 0,
      };

      if (this.isAdmin) {
        formDataToSend.manufacturers = this.$refs['manufacturer-discount'].getDiscounts();
        formDataToSend.categories = this.$refs['category-discount'].getDiscounts();
        formDataToSend.products = this.$refs['product-discount'].getDiscounts();
      }

      return formDataToSend;
    },
    loadAdditionalData() {
      if (this.isAdmin) {
        FormUtils.loadAdditionalData(this, {
          endpoint: '/api/users/items',
          method: 'get',
          outputKey: 'workers',
          loadedKey: 'loadedWorkers',
          errorKey: 'loadError',
          params: {
            pagination: 0,
            where: {
              type: 'WORKER',
            },
          },
          successAction: () => {
            this.checkAssignedWorker();
          },
        });
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/manufacturers/items',
        method: 'get',
        outputKey: 'allManufacturers',
        loadedKey: 'loadedManufacturers',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/categories/items',
        method: 'get',
        outputKey: 'allCategories',
        loadedKey: 'loadedCategories',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/products/items',
        method: 'get',
        outputKey: 'allProducts',
        loadedKey: 'loadedProducts',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/payments/items',
        method: 'get',
        outputKey: 'allPayments',
        loadedKey: 'loadedPayments',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        }
      });
    },
    showAddContactPerson() {
      this.$bus.$emit('client-contact-person-popup-show', this.data.id);
    },
    showAddDeliveryAddress() {
      this.$bus.$emit('client-delivery-address-popup-show', this.data.id);
    },
    removeContact(contactId) {
      this.removeContactInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/api/contacts/contacts/delete/',
        id: contactId,
        successTitle: 'Usunięto kontakt',
        successText: 'Wybrany kontakt został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć ten kontakt?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie kontaktu nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeContactInProgress = false;
          this.reloadContactList();
        },
        cancelAction: () => {
          this.removeContactInProgress = false;
        },
      });
    },
    removeAddress(addressId) {
      this.removeAddressInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/api/contacts/addresses/delete/',
        id: addressId,
        successTitle: 'Usunięto adres',
        successText: 'Wybrany adres został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć ten adres?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie adresu nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeAddressInProgress = false;
          this.reloadAddressList();
        },
        cancelAction: () => {
          this.removeAddressInProgress = false;
        },
      });
    },
    reloadContactList() {
      this.loadedContacts = false;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/contacts/contacts/items',
        method: 'get',
        outputKey: 'data',
        outputKeyIndex: 'contacts',
        loadedKey: 'loadedContacts',
        errorKey: 'loadError',
        params: {
          pagination: 0,
          where: {
            customer_id: this.data.id,
          },
        },
      });
    },
    reloadAddressList() {
      this.loadedAddresses = false;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/contacts/addresses/items',
        method: 'get',
        outputKey: 'data',
        outputKeyIndex: 'addresses',
        loadedKey: 'loadedAddresses',
        errorKey: 'loadError',
        params: {
          pagination: 0,
          where: {
            customer_id: this.data.id,
          },
        },
      });
    },
    checkAssignedWorker () {
      Vue.nextTick(() => {
        if (this.isEdit && this.loadedWorkers && this.workers && this.workers.length) {
          let filteredWorkers = this.workers.filter(worker => worker.id === this.data.worker_id);
          if (!filteredWorkers.length) {
            Vue.set(this.data, 'worker_id', null);
          }
        }
      });
    },
    fetchInvoiceInfo () {
      this.nipResultsLoading = true;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/contacts/find-by-nip',
        method: 'post',
        outputKey: 'invoiceInfo',
        loadedKey: 'loadedInvoiceInfo',
        errorKey: 'loadError',
        params: {
          nip: this.nipLoadData
        },
        successAction: () => {
          this.nipResultsLoading = false;
          Vue.nextTick(() => {
            if (!this.invoiceInfo) {
              this.nipResultsEmpty = true;
              this.setInvoiceInfo();
              return
            }

            this.setInvoiceInfo(this.invoiceInfo);
          });
        },
        failAction: () => {
          this.nipResultsLoading = false;
          this.nipResultsEmpty = true;
        }
      });
    },
    setInvoiceInfo (info = null) {
      if (!info) {
        return
      }

      if (this.isEdit) {
        // in edit mode only not empty values returned from NIP24 will be applied
        this.data.name = info.name || this.data.name;
        this.data.surname = (info.firstname + ' ' + info.lastname).trim() || this.data.surname;
        this.data.street = info.street || this.data.street;
        this.data.building_number = info.streetNumber || this.data.building_number;
        this.data.house_number = info.houseNumber || this.data.house_number;
        this.data.post_code = info.postCode || this.data.post_code;
        this.data.city = info.city || this.data.city;
        this.data.phone = info.phone || this.data.phone;
        this.data.email = info.email || this.data.email;

        this.data.invoice_company = info.name || this.data.invoice_company;
        this.data.invoice_name = info.firstname || this.data.invoice_name;
        this.data.invoice_surname = info.lastname || this.data.invoice_surname;
        this.data.invoice_street = info.street || this.data.invoice_street;
        this.data.invoice_building_number = info.streetNumber || this.data.invoice_building_number;
        this.data.invoice_house_number = info.houseNumber || this.data.invoice_house_number;
        this.data.invoice_post_code = info.postCode || this.data.invoice_post_code;
        this.data.invoice_city = info.city || this.data.invoice_city;
        this.data.invoice_nip = info.nip || this.data.invoice_nip;
      } else {
        // in NON edit mode (new client) all fields will be overwritten by date from NIP24
        this.data.name = info.name;
        this.data.surname = (info.firstname + ' ' + info.lastname).trim();
        this.data.street = info.street;
        this.data.building_number = info.streetNumber;
        this.data.house_number = info.houseNumber;
        this.data.post_code = info.postCode;
        this.data.city = info.city;
        this.data.phone = info.phone;
        this.data.email = info.email;

        this.data.invoice_company = info.name;
        this.data.invoice_name = info.firstname;
        this.data.invoice_surname = info.lastname;
        this.data.invoice_street = info.street;
        this.data.invoice_building_number = info.streetNumber;
        this.data.invoice_house_number = info.houseNumber;
        this.data.invoice_post_code = info.postCode;
        this.data.invoice_city = info.city;
        this.data.invoice_nip = info.nip;
      }
    }
  },
  beforeDestroy() {
    this.$bus.$off('client-contact-person-added', this.reloadContactList);
    this.$bus.$off('client-delivery-address-added', this.reloadAddressList);
  },
};
</script>

<style lang="scss" scoped>
  .nipSearchBtn {
    margin-bottom: auto;
    margin-top: 31px;
  }

  @media screen and (max-width: 768px) {
    .nipSearchBtn {
      margin-bottom: 15px;
      margin-top: 0;
    }
  }
</style>
